import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ApiService } from 'src/app/core/services/api.service';
import { HelperService } from 'src/app/core/services/helper.service';
import { UserProxy } from 'src/app/core/store/user/user.proxy';
import {
  DEEPMATCH_WEIGHTS_CLAIM,
  DeepMatchEffectiveSettings,
  DeepMatchPriorityWeights
} from 'src/app/core/models/deepmatch-priority.model';

type WeightKey = keyof DeepMatchPriorityWeights;

const SLIDERS: { key: WeightKey; labelKey: string }[] = [
  { key: 'workPlace', labelKey: 'MatchPriority.Workplace' },
  { key: 'companyDomain', labelKey: 'MatchPriority.CompanyDomain' },
  { key: 'language', labelKey: 'MatchPriority.Language' },
  { key: 'relocation', labelKey: 'MatchPriority.Relocation' },
  { key: 'employmentType', labelKey: 'MatchPriority.EmploymentType' },
  { key: 'education', labelKey: 'MatchPriority.Education' },
  { key: 'userSkillValidation', labelKey: 'MatchPriority.SkillValidation' },
  { key: 'hardSkills', labelKey: 'MatchPriority.HardSkills' }
];

@Component({
  selector: 'app-match-priority-popover',
  templateUrl: './match-priority-popover.component.html',
  styleUrls: ['./match-priority-popover.component.scss']
})
export class MatchPriorityPopoverComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() scope: 'account' | 'vacancy' = 'account';
  @Input() vacancyId?: string;

  @Output() close = new EventEmitter<void>();
  @Output() rescoreStarted = new EventEmitter<void>();

  readonly criteriaList = SLIDERS;

  isLoading = true;
  isSaving = false;
  errorMessage: string | null = null;

  weights: DeepMatchPriorityWeights = this.mapWeights({});
  private originalWeights: DeepMatchPriorityWeights = this.mapWeights({});
  source = 'Default';

  shellTop = 0;
  shellLeft = 0;
  arrowInset = 16;
  bodyMaxHeight: number | null = null;
  placeAbove = false;
  isPositioned = false;
  isSheet = false;

  showConfirmPopup = false;
  pendingAction: 'save' | 'reset' | null = null;
  confirmTitle = '';
  confirmMsg = '';
  confirmOkLabel = '';

  private destroy$ = new Subject<void>();
  private triggerEl: HTMLElement | null = null;
  private readonly panelWidth = 280;
  private readonly mobileMax = 640;

  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private userProxy: UserProxy,
    private elRef: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.isSheet = window.innerWidth <= this.mobileMax;
    this.loadWeights();
  }

  ngAfterViewInit(): void {
    const wrapper = this.elRef.nativeElement.parentElement;
    this.triggerEl = (wrapper?.querySelector('button') as HTMLElement) || wrapper;
    document.body.appendChild(this.elRef.nativeElement);
    this.helperService.blockScroll$.next(true);

    setTimeout(() => this.positionUnderButton());
  }

  ngOnDestroy(): void {
    this.helperService.blockScroll$.next(false);
    const host = this.elRef.nativeElement;
    if (host.parentNode) {
      host.parentNode.removeChild(host);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.positionUnderButton();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.showConfirmPopup) {
      this.close.emit();
    }
  }

  get totalWeight(): number {
    return this.criteriaList.reduce((sum, c) => sum + (this.weights[c.key] || 0), 0);
  }

  get isWeightTotalInvalid(): boolean {
    return this.totalWeight > 100;
  }

  get hasWeightChanges(): boolean {
    return this.criteriaList.some(
      (c) => (this.weights[c.key] || 0) !== (this.originalWeights[c.key] || 0)
    );
  }

  get canSave(): boolean {
    return this.hasWeightChanges && this.totalWeight > 0 && !this.isWeightTotalInvalid;
  }

  get canReset(): boolean {
    return this.scope === 'vacancy'
      ? this.source === 'Vacancy'
      : this.source === 'Account';
  }

  get sourceBadgeLabel(): string {
    if (this.source === 'Vacancy') return 'MatchPriority.SourceVacancy';
    if (this.source === 'Account') return 'MatchPriority.SourceAccount';
    return 'MatchPriority.SourceDefault';
  }

  get sourceBadgeClass(): string {
    if (this.source === 'Vacancy') return 'is-vacancy';
    if (this.source === 'Account') return 'is-account';
    return 'is-default';
  }

  get saveLabelKey(): string {
    return this.scope === 'vacancy'
      ? 'MatchPriority.ConfirmAndRecalculate'
      : 'MatchPriority.ConfirmAndSave';
  }

  get resetLabelKey(): string {
    return this.scope === 'vacancy'
      ? 'MatchPriority.ResetToAccount'
      : 'MatchPriority.ResetToDefault';
  }

  loadWeights(): void {
    this.isLoading = true;
    this.errorMessage = null;

    const req$ =
      this.scope === 'vacancy' && this.vacancyId
        ? this.loadVacancyWeights(this.vacancyId)
        : this.loadWeightsFromClaims();

    req$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: DeepMatchEffectiveSettings) => {
        this.weights = this.mapWeights(res?.weights || res);
        this.originalWeights = { ...this.weights };
        this.source = res?.source || 'Default';
        this.isLoading = false;
        setTimeout(() => this.positionUnderButton());
      },
      error: (err) => {
        this.isLoading = false;
        this.setErrorMessage(err, 'MatchPriority.LoadError');
        setTimeout(() => this.positionUnderButton());
      }
    });
  }

  onWeightChange(key: WeightKey, event: Event): void {
    const n = parseInt((event.target as HTMLInputElement).value, 10);
    this.weights[key] = isNaN(n) ? 0 : Math.max(0, Math.min(100, n));
    if (this.isWeightTotalInvalid) {
      this.errorMessage = 'MatchPriority.ErrorWeightTotalExceeds';
    } else if (this.totalWeight > 0) {
      this.errorMessage = null;
    }
  }

  requestSaveConfirmation(): void {
    if (this.isWeightTotalInvalid) {
      this.errorMessage = 'MatchPriority.ErrorWeightTotalExceeds';
      return;
    }
    if (!this.canSave) {
      this.errorMessage = 'MatchPriority.ErrorZeroSum';
      return;
    }
    this.openConfirm('save');
  }

  requestResetConfirmation(): void {
    this.openConfirm('reset');
  }

  onConfirmResult(result: boolean | { msg: any } | null): void {
    if (result !== true) {
      this.showConfirmPopup = false;
      this.pendingAction = null;
      this.reLockScroll();
      return;
    }
    this.showConfirmPopup = false;
    this.reLockScroll();
    this.isSaving = true;
    this.errorMessage = null;
    if (this.pendingAction === 'save') {
      this.executeSave();
    } else if (this.pendingAction === 'reset') {
      this.executeReset();
    }
  }

  onBackdropClick(): void {
    if (!this.showConfirmPopup) {
      this.close.emit();
    }
  }

  onClose(event?: Event): void {
    event?.stopPropagation();
    this.close.emit();
  }

  private openConfirm(action: 'save' | 'reset'): void {
    this.pendingAction = action;
    const vac = this.scope === 'vacancy';

    if (action === 'save') {
      this.confirmTitle = vac ? 'MatchPriority.ConfirmSaveVacancyTitle' : 'MatchPriority.ConfirmSaveAccountTitle';
      this.confirmMsg = vac ? 'MatchPriority.ConfirmSaveVacancyMsg' : 'MatchPriority.ConfirmSaveAccountMsg';
      this.confirmOkLabel = this.saveLabelKey;
    } else {
      this.confirmTitle = vac ? 'MatchPriority.ConfirmResetVacancyTitle' : 'MatchPriority.ConfirmResetAccountTitle';
      this.confirmMsg = vac ? 'MatchPriority.ConfirmResetVacancyMsg' : 'MatchPriority.ConfirmResetAccountMsg';
      this.confirmOkLabel = this.resetLabelKey;
    }
    this.showConfirmPopup = true;
  }

  private executeSave(): void {
    const isVacancy = this.scope === 'vacancy' && !!this.vacancyId;
    const req$ = isVacancy
      ? this.apiService.saveVacancyDeepMatchWeights(this.vacancyId!, this.weights).pipe(
          switchMap(() => this.rescoreVacancy(this.vacancyId!))
        )
      : this.withAccountId((accountId) =>
          this.apiService.saveAccountDeepMatchWeights(accountId, this.weights)
        );

    req$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.finishAndClose(),
      error: (err) => this.failAction(err, 'MatchPriority.SaveError')
    });
  }

  private executeReset(): void {
    const isVacancy = this.scope === 'vacancy' && !!this.vacancyId;
    const req$ = isVacancy
      ? this.apiService.resetVacancyDeepMatchWeights(this.vacancyId!).pipe(
          switchMap(() => this.rescoreVacancy(this.vacancyId!))
        )
      : this.withAccountId((accountId) =>
          this.apiService.resetAccountDeepMatchWeights(accountId)
        );

    req$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.finishAndClose(),
      error: (err) => this.failAction(err, 'MatchPriority.ResetError')
    });
  }

  private loadVacancyWeights(vacancyId: string): Observable<DeepMatchEffectiveSettings> {
    return this.apiService.getVacancyDeepMatchSettings(vacancyId).pipe(
      map((res) => ({
        source: 'Vacancy',
        weights: this.mapWeights(res?.weights || res)
      })),
      catchError((err) =>
        err?.status === 404 ? this.loadWeightsFromClaims() : throwError(() => err)
      )
    );
  }

  private loadWeightsFromClaims(): Observable<DeepMatchEffectiveSettings> {
    return from(this.userProxy.getUser()).pipe(
      catchError(() => of(null)),
      switchMap(() =>
        this.userProxy.userClaims$.pipe(
          filter((claims) => claims !== null),
          take(1),
          map((claims) => this.mapDeepMatchClaim(claims))
        )
      )
    );
  }

  private mapDeepMatchClaim(claims: any): DeepMatchEffectiveSettings {
    const claim = (Array.isArray(claims) ? claims : []).find(
      (c: any) => c?.type === DEEPMATCH_WEIGHTS_CLAIM
    );
    let raw: any = claim?.value;
    try {
      if (typeof raw === 'string') {
        raw = JSON.parse(raw);
        if (typeof raw === 'string') {
          raw = JSON.parse(raw);
        }
      }
    } catch {
      raw = {};
    }
    const source = (claim?.properties?.Source || '').toString();
    return {
      source: source === 'Account' ? 'Account' : 'Default',
      weights: this.mapWeights(raw)
    };
  }

  private withAccountId<T>(fn: (accountId: string) => Observable<T>): Observable<T> {
    return this.userProxy.UserAccountId$.pipe(
      take(1),
      switchMap((accountId) =>
        accountId
          ? fn(accountId)
          : throwError(() => new Error('Account id is not available'))
      )
    );
  }

  private rescoreVacancy(vacancyId: string) {
    return this.apiService.rescoreVacancyDeepMatch(vacancyId).pipe(
      tap(() => this.rescoreStarted.emit()),
      catchError((err) => {
        if (err?.status === 409) {
          return throwError(() => err);
        }
        return of(null);
      })
    );
  }

  private failAction(err: any, fallbackKey: string): void {
    this.isSaving = false;
    this.pendingAction = null;
    if (err?.status === 409) {
      this.errorMessage = 'MatchPriority.RescoreConflict';
    } else {
      this.setErrorMessage(err, fallbackKey);
    }
    this.reLockScroll();
  }

  private finishAndClose(): void {
    this.isSaving = false;
    this.pendingAction = null;
    this.close.emit();
  }

  private setErrorMessage(err: any, fallbackKey: string): void {
    if (err) {
      console.error(err);
    }
    this.errorMessage = fallbackKey;
  }

  private reLockScroll(): void {
    setTimeout(() => this.helperService.blockScroll$.next(true));
  }

  private positionUnderButton(): void {
    this.isSheet = window.innerWidth <= this.mobileMax;
    if (this.isSheet || !this.triggerEl) {
      this.bodyMaxHeight = null;
      this.placeAbove = false;
      this.isPositioned = true;
      return;
    }

    const r = this.triggerEl.getBoundingClientRect();
    const pad = 12;
    const gap = 10;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(this.panelWidth, vw - pad * 2);
    const chrome = 148;

    let left = r.right - w;
    if (left < pad) {
      left = r.left;
    }
    this.shellLeft = Math.round(Math.max(pad, Math.min(left, vw - w - pad)));
    this.arrowInset = Math.max(
      14,
      Math.min(w - 14, Math.round(r.left + r.width / 2 - this.shellLeft))
    );

    const spaceBelow = vh - r.bottom - gap - pad - 16;
    const spaceAbove = r.top - gap - pad;
    this.placeAbove = spaceBelow < 220 && spaceAbove > spaceBelow;

    const space = this.placeAbove ? spaceAbove : spaceBelow;
    this.bodyMaxHeight = space > chrome + 340 ? null : Math.max(96, Math.floor(space - chrome));

    this.shellTop = this.placeAbove
      ? Math.round(r.top - gap - (chrome + (this.bodyMaxHeight ?? 340)))
      : Math.round(r.bottom + gap);

    requestAnimationFrame(() => {
      const shell = this.elRef.nativeElement.querySelector(
        '.match-priority-shell'
      ) as HTMLElement | null;
      if (shell && this.triggerEl && !this.isSheet) {
        const rect = this.triggerEl.getBoundingClientRect();
        const h = shell.offsetHeight;
        this.shellTop = this.placeAbove
          ? Math.round(rect.top - gap - h)
          : Math.round(rect.bottom + gap);
      }
      this.isPositioned = true;
    });
  }

  private mapWeights(raw: any): DeepMatchPriorityWeights {
    const r = raw || {};
    const num = (key: string, fallback = 0) => {
      const pascal = key[0].toUpperCase() + key.slice(1);
      const v = Number(r[key] ?? r[pascal]);
      return isNaN(v) ? fallback : Math.max(0, Math.min(100, Math.round(v)));
    };

    return {
      workPlace: num('workPlace'),
      companyDomain: num('companyDomain'),
      language: num('language'),
      relocation: num('relocation'),
      employmentType: num('employmentType'),
      education: num('education'),
      userSkillValidation: num('userSkillValidation'),
      hardSkills: num('hardSkills'),
      softSkills: num('softSkills'),
      requiredExperience: num('requiredExperience'),
      salary: num('salary'),
      verifiedUser: num('verifiedUser'),
      companySize: num('companySize')
    };
  }
}
